import { PROTOCOL_VERSION } from './constants';
import type { CommandDraft } from '../sim/commands';
import type { MatchConfig, PlayerSlot } from '../sim/types';

/*
 * פרוטוקול המולטיפלייר — דאטה מוקלדת, בלי שום תלות ברשת.
 *
 * הקידוד והפענוח הם פונקציות טהורות על מחרוזת אחת, ולכן כל הפרוטוקול נבדק **בלי
 * שרת, בלי WebSocket ובלי טיימרים**. השכבה שמעבירה את המחרוזת (`Transport`) לא
 * יודעת מה יש בה, וההיפך.
 *
 * **הפענוח מאמת, לא סומך.** צד שני יכול לשלוח כל דבר — הודעה שבורה שנכנסת
 * ל-`step` היא desync במקרה הטוב וקריסה במקרה הרע. לכן כל שדה נבדק, ופקודה אחת
 * פסולה פוסלת את כל הודעת התור: תור שנקלט חלקית גרוע מתור שלא נקלט בכלל.
 *
 * **מה לא נשלח בכוונה:** `tick`, `player` ו-`seq` של כל פקודה. הטיק נגזר ממספר
 * התור, השחקן מההודעה, וה-seq מהמיקום במערך. שדה שאינו עובר ברשת הוא שדה ששני
 * הצדדים לא יכולים לחלוק עליו.
 */

export interface JoinMessage {
    readonly kind: 'join';
    readonly protocol: number;
    readonly room: string;
    readonly clientId: string;
}

/**
 * מצב החדר, כפי שה-relay רואה אותו.
 *
 * **זו ההודעה היחידה שה-Worker מחליט על תוכנה מעצמו** מלבד `start`, והיא קיימת
 * כדי שהשגיאות של הלובי יגיעו באותו ערוץ כמו כל השאר: "אין חדר כזה" ו"החדר מלא"
 * הן תשובות של השרת, ולא כשל תעבורה — ובלי הודעה מפורשת הן היו מגיעות ללקוח
 * כניתוק אנונימי שאי אפשר להסביר למשתמש.
 */
export type LobbyState = 'waiting' | 'full' | 'unknown';

export interface LobbyMessage {
    readonly kind: 'lobby';
    readonly protocol: number;
    readonly state: LobbyState;
    readonly room: string;
    /** כמה מחוברים בחדר כרגע, כולל אותנו. */
    readonly players: number;
}

/**
 * הכרזת פתיחת המשחק. `seats[i]` הוא מזהה הלקוח שיושב במשבצת השחקן i — כך כל צד
 * מוצא את מספר השחקן שלו בעצמו, ואין הודעה שונה לכל נמען.
 */
export interface StartMessage {
    readonly kind: 'start';
    readonly protocol: number;
    readonly config: MatchConfig;
    readonly seats: readonly string[];
}

/** פקודות תור אחד. הודעה ריקה היא "אין לי פקודות", והיא חובה — היא מה שמשחרר את הטיק. */
export interface TurnMessage {
    readonly kind: 'turn';
    readonly protocol: number;
    readonly player: number;
    readonly turn: number;
    readonly commands: readonly CommandDraft[];
}

export interface HashMessage {
    readonly kind: 'hash';
    readonly protocol: number;
    readonly player: number;
    readonly tick: number;
    readonly hash: number;
}

export interface LeaveMessage {
    readonly kind: 'leave';
    readonly protocol: number;
    readonly player: number;
    readonly reason: string;
}

export type NetMessage =
    | JoinMessage
    | LobbyMessage
    | StartMessage
    | TurnMessage
    | HashMessage
    | LeaveMessage;

export type DecodeResult =
    | { readonly ok: true; readonly message: NetMessage }
    | { readonly ok: false; readonly reason: string };

export function encode(message: NetMessage): string {
    return JSON.stringify(message);
}

export function decode(data: string): DecodeResult {
    let raw: unknown;
    try {
        raw = JSON.parse(data) as unknown;
    } catch {
        return fail('לא JSON תקין');
    }
    if (!isObject(raw)) return fail('ההודעה אינה אובייקט');

    /*
     * גרסת הפרוטוקול נבדקת לפני הכול. שני צדדים בגרסאות שונות עלולים להסכים על
     * צורת ההודעה ולחלוק על המשמעות — וזה בדיוק desync שקשה לאתר.
     */
    const protocol = readInt(raw, 'protocol');
    if (protocol === null) return fail('חסרה גרסת פרוטוקול');
    if (protocol !== PROTOCOL_VERSION) {
        return fail(`גרסת פרוטוקול ${protocol}, המשחק מדבר ${PROTOCOL_VERSION}`);
    }

    switch (raw['kind']) {
        case 'join':
            return decodeJoin(raw, protocol);
        case 'lobby':
            return decodeLobby(raw, protocol);
        case 'start':
            return decodeStart(raw, protocol);
        case 'turn':
            return decodeTurn(raw, protocol);
        case 'hash':
            return decodeHash(raw, protocol);
        case 'leave':
            return decodeLeave(raw, protocol);
        default:
            return fail(`סוג הודעה לא מוכר: ${String(raw['kind'])}`);
    }
}

function decodeJoin(raw: Record<string, unknown>, protocol: number): DecodeResult {
    const room = raw['room'];
    const clientId = raw['clientId'];
    if (typeof room !== 'string' || typeof clientId !== 'string') return fail('join פגום');
    return { ok: true, message: { kind: 'join', protocol, room, clientId } };
}

function decodeLobby(raw: Record<string, unknown>, protocol: number): DecodeResult {
    const state = raw['state'];
    const room = raw['room'];
    const players = readInt(raw, 'players');
    if (state !== 'waiting' && state !== 'full' && state !== 'unknown') return fail('lobby פגום');
    if (typeof room !== 'string' || players === null) return fail('lobby פגום');
    return { ok: true, message: { kind: 'lobby', protocol, state, room, players } };
}

function decodeStart(raw: Record<string, unknown>, protocol: number): DecodeResult {
    const config = decodeConfig(raw['config']);
    const seats = raw['seats'];
    if (config === null) return fail('הגדרת משחק פגומה');
    if (!Array.isArray(seats) || !seats.every((s) => typeof s === 'string')) {
        return fail('רשימת המשבצות פגומה');
    }
    return { ok: true, message: { kind: 'start', protocol, config, seats: seats as string[] } };
}

function decodeTurn(raw: Record<string, unknown>, protocol: number): DecodeResult {
    const player = readInt(raw, 'player');
    const turn = readInt(raw, 'turn');
    const list = raw['commands'];
    if (player === null || player < 0) return fail('מספר שחקן פגום');
    if (turn === null || turn < 0) return fail('מספר תור פגום');
    if (!Array.isArray(list)) return fail('רשימת פקודות פגומה');

    const commands: CommandDraft[] = [];
    for (const item of list) {
        const command = decodeCommand(item);
        // תור שנקלט חלקית מפצל את המשחק בשקט; תור שנדחה נראה מיד.
        if (command === null) return fail('פקודה פגומה בתוך התור');
        commands.push(command);
    }
    return { ok: true, message: { kind: 'turn', protocol, player, turn, commands } };
}

function decodeHash(raw: Record<string, unknown>, protocol: number): DecodeResult {
    const player = readInt(raw, 'player');
    const tick = readInt(raw, 'tick');
    const hash = readInt(raw, 'hash');
    if (player === null || tick === null || hash === null) return fail('hash פגום');
    return { ok: true, message: { kind: 'hash', protocol, player, tick, hash } };
}

function decodeLeave(raw: Record<string, unknown>, protocol: number): DecodeResult {
    const player = readInt(raw, 'player');
    const reason = raw['reason'];
    if (player === null || typeof reason !== 'string') return fail('leave פגום');
    return { ok: true, message: { kind: 'leave', protocol, player, reason } };
}

/**
 * פענוח פקודה בודדת.
 *
 * הצורה כאן חוזרת על `Command` שב-`sim/commands.ts` בכוונה: זו הנקודה שבה דאטה
 * מבחוץ הופכת לטיפוס של הסימולציה, והיא חייבת לבדוק שדה-שדה. הקומפיילר יתריע אם
 * ייווסף סוג פקודה חדש ולא ייווסף כאן, כי `CommandDraft` היא יוניה סגורה.
 */
export function decodeCommand(raw: unknown): CommandDraft | null {
    if (!isObject(raw)) return null;
    const unitIds = raw['unitIds'];
    if (!isIntArray(unitIds)) return null;

    const tileX = readInt(raw, 'tileX');
    const tileY = readInt(raw, 'tileY');

    switch (raw['kind']) {
        case 'move':
            if (tileX === null || tileY === null) return null;
            return { kind: 'move', unitIds, tileX, tileY };
        case 'attackMove':
            if (tileX === null || tileY === null) return null;
            return { kind: 'attackMove', unitIds, tileX, tileY };
        case 'place':
            if (tileX === null || tileY === null) return null;
            return { kind: 'place', unitIds, tileX, tileY };
        case 'attack': {
            const targetId = readInt(raw, 'targetId');
            if (targetId === null) return null;
            return { kind: 'attack', unitIds, targetId };
        }
        case 'stop':
            return { kind: 'stop', unitIds };
        case 'queue': {
            const itemKind = readInt(raw, 'itemKind');
            const itemType = readInt(raw, 'itemType');
            if (itemKind === null || itemType === null) return null;
            return { kind: 'queue', unitIds, itemKind, itemType };
        }
        case 'cancel': {
            /*
             * **`queueIndex` ולא `itemKind`.** הפרוטוקול נכתב כשהיו שני תורים
             * בלבד; מרגע שיש תור לכל מבנה ייצור, "בטל יחידה" אינו מזהה תור אחד.
             */
            const queueIndex = readInt(raw, 'queueIndex');
            if (queueIndex === null) return null;
            return { kind: 'cancel', unitIds, queueIndex };
        }
        case 'setRally': {
            const buildingId = readInt(raw, 'buildingId');
            if (buildingId === null || tileX === null || tileY === null) return null;
            return { kind: 'setRally', unitIds, buildingId, tileX, tileY };
        }
        case 'sell': {
            const buildingId = readInt(raw, 'buildingId');
            if (buildingId === null) return null;
            return { kind: 'sell', unitIds, buildingId };
        }
        default:
            return null;
    }
}

function decodeConfig(raw: unknown): MatchConfig | null {
    if (!isObject(raw)) return null;
    const seed = readInt(raw, 'seed');
    const mapId = raw['mapId'];
    const list = raw['slots'];
    if (seed === null || typeof mapId !== 'string' || !Array.isArray(list)) return null;

    const slots: PlayerSlot[] = [];
    for (const item of list) {
        if (!isObject(item)) return null;
        const kind = item['kind'];
        if (kind !== 'human' && kind !== 'ai' && kind !== 'remote') return null;
        const difficulty = item['difficulty'];
        if (difficulty === undefined) {
            slots.push({ kind });
        } else if (typeof difficulty === 'number' && Number.isInteger(difficulty)) {
            slots.push({ kind, difficulty });
        } else {
            return null;
        }
    }
    if (slots.length === 0) return null;
    return { seed, mapId, slots };
}

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isIntArray(value: unknown): value is number[] {
    return Array.isArray(value) && value.every((n) => typeof n === 'number' && Number.isInteger(n));
}

function readInt(source: Record<string, unknown>, key: string): number | null {
    const value = source[key];
    return typeof value === 'number' && Number.isInteger(value) ? value : null;
}

function fail(reason: string): DecodeResult {
    return { ok: false, reason };
}
